import { Controller } from "stimulus"

/**
 * Enables a Bulk Merge group's "Merge records" button only once a parent and
 * at least one other record are chosen, keeps the parent's own row out of the
 * records to merge, checks records from the header's select-all box (up to one
 * less than the cap, so the parent still fits) or clears them all, and confirms
 * before submitting.
 *
 *   <form data-controller="bulk-merge" data-bulk-merge-max="25">
 *     <p data-target="bulk-merge.status"></p>
 *     <input type="checkbox" data-target="bulk-merge.all" data-action="bulk-merge#toggleAll">
 *     <input type="submit" data-target="bulk-merge.submit" data-action="click->bulk-merge#confirm">
 *     <input type="checkbox" data-target="bulk-merge.record" data-action="bulk-merge#refresh">
 *     <input type="radio" data-target="bulk-merge.parent" data-action="bulk-merge#refresh">
 *   </form>
 */
export default class extends Controller {
  static targets = ["record", "parent", "submit", "status", "all"]

  connect() {
    this.refresh()
  }

  refresh() {
    const parentId = this.parentId()
    this.recordTargets.forEach((checkbox) => {
      const isParent = checkbox.value === parentId
      if (isParent) checkbox.checked = false
      checkbox.disabled = isParent
    })
    this.refreshAll()
    const problem = this.problem()
    this.submitTarget.disabled = problem !== null
    this.statusTarget.textContent = problem || this.ready()
  }

  // Checking fills the set in page order up to the select-all limit, keeping
  // any record already checked; clearing unchecks every record.
  toggleAll() {
    if (this.allTarget.checked) {
      this.selectableRecords().forEach((checkbox) => {
        if (!checkbox.checked && this.selectedCount() < this.selectAllLimit()) checkbox.checked = true
      })
    } else {
      this.selectableRecords().forEach((checkbox) => { checkbox.checked = false })
    }
    this.refresh()
  }

  // Checked once the records fill what select all would check, partly checked
  // when only some are.
  refreshAll() {
    if (!this.hasAllTarget) return

    const target = Math.min(this.selectableRecords().length, this.selectAllLimit())
    const selected = this.selectedCount()
    this.allTarget.checked = target > 0 && selected >= target
    this.allTarget.indeterminate = selected > 0 && selected < target
  }

  ready() {
    const selected = this.selectedCount()
    const message = `Merge ${this.describe(selected)} into the parent.`
    const remaining = this.selectableRecords().length - selected
    if (selected < this.selectAllLimit() || remaining === 0) return message

    return `${message} That is the most at once; merge the other ${remaining} afterwards.`
  }

  confirm(event) {
    if (window.confirm(`Merge ${this.describe(this.selectedCount())} into the parent? This cannot be undone.`)) return

    event.preventDefault()
  }

  problem() {
    if (this.parentId() === null) return "Choose a parent and the records to merge into it."
    if (this.selectedCount() === 0) return "Select the records to merge into the parent."
    if (this.selectedCount() + 1 > this.max()) return `Merge at most ${this.max()} records at a time, including the parent.`

    return null
  }

  parentId() {
    const parent = this.parentTargets.find((radio) => radio.checked)
    return parent ? parent.value : null
  }

  selectableRecords() {
    return this.recordTargets.filter((checkbox) => !checkbox.disabled)
  }

  selectedCount() {
    return this.selectableRecords().filter((checkbox) => checkbox.checked).length
  }

  max() {
    return parseInt(this.data.get("max"), 10)
  }

  // The parent counts toward the cap, so select all leaves room for it.
  selectAllLimit() {
    return this.max() - 1
  }

  describe(count) {
    return count === 1 ? "1 record" : `${count} records`
  }
}
